import React, { PureComponent, Fragment } from 'react';
import t from 'prop-types';
import cn from 'classnames';

import { Cross } from '@mos-cat/icon';
import Tooltip, { POSITIONS, THEME } from '@mos-cat/tooltip';

import './styles/TextFieldWrapper.pcss';
import ERROR_LOCATION from './private/constants';

/**
 * @class
 */
class TextFieldWrapper extends PureComponent {
  showError(type) {
    const { errorLocation, error } = this.props;
    return errorLocation === type && typeof error === 'string';
  }

  /**
   * @returns {React.Element}
   */
  render() {
    const {
      className,
      children,
      error,
      placeholder,
      icon,
      size,
      onClear,
      tooltipPosition,
    } = this.props;

    const child = React.Children.only(children);
    const { className: childClassName, value, disabled } = child.props;
    const nextProps = {
      ...child.props,
      className: cn(childClassName, 'TextFieldWrapper__control'),
    };

    const classNames = cn(
      {
        TextFieldWrapper: true,
        [`TextFieldWrapper_size_${size.toLowerCase()}`]: true,
        TextFieldWrapper_hasValue: !!value,
        TextFieldWrapper_hasError: !!error,
        TextFieldWrapper_disabled: disabled,
      },
      className
    );

    const tooltipClassNames = cn({
      Tooltip_TextFieldWrapper: true,
      [`Tooltip_${className}`]: className,
    });
    const isErrorString = typeof error === 'string' && error;

    return (
      <Tooltip
        className={tooltipClassNames}
        content={isErrorString}
        position={tooltipPosition}
        open={this.showError(ERROR_LOCATION.TOOLTIP)}
        theme={THEME.DANGER}
      >
        <Fragment>
          <label className={classNames}>
            <div className="TextFieldWrapper__main">
              <span className="TextFieldWrapper__placeholder">
                {this.showError(ERROR_LOCATION.PLACEHOLDER)
                  ? error
                  : placeholder}
              </span>
              {React.cloneElement(child, nextProps)}
            </div>
            {onClear && (
              <Cross onClick={onClear} className="TextFieldWrapper__clear" />
            )}
            {icon}
            {this.showError(ERROR_LOCATION.TEXT_BELOW) && (
              <div className="TextFieldWrapper__errorText">{error}</div>
            )}
          </label>
        </Fragment>
      </Tooltip>
    );
  }
}

TextFieldWrapper.SIZE = {
  BIG: 'BIG',
  STANDARD: 'STANDARD',
  SMALL: 'SMALL',
};

TextFieldWrapper.ERROR_LOCATION = ERROR_LOCATION;
TextFieldWrapper.POSITIONS = POSITIONS;

if (process.env.NODE_ENV === 'development') {
  TextFieldWrapper.propTypes = {
    /**
     * Единственный ребёнок, в который передаются `value` и `disabled`.
     *
     * Обязательно должен быть фокусируемым.
     */
    children: t.node.isRequired,
    className: t.string,
    icon: t.node,
    placeholder: t.string,
    /**
     * Расположение ошибки - placeholder, текст снизу или в Tooltip
     * Доступны как экспортируемые значения TextFieldWrapper.ERROR_LOCATION
     *
     * default ERROR_LOCATION.PLACEHOLDER
     */
    errorLocation: t.oneOf(Object.values(ERROR_LOCATION)),
    /**
     * Расположение Tooltip
     * Доступны как экспортируемые значения TextFieldWrapper.POSITIONS
     *
     * default POSITIONS.TOP
     */
    tooltipPosition: t.oneOf(Object.values(POSITIONS)),
    /**
     * Размеры инпута
     * Доступны как экспортируемые значения TextFieldWrapper.SIZE
     */
    size: t.oneOf(Object.keys(TextFieldWrapper.SIZE)),
    /**
     * Индикатор ошибки. Если строка, выводится вместо плейсхолдера.
     *
     */
    error: t.oneOfType([t.bool, t.string]),
    onClear: t.func,
  };
}

TextFieldWrapper.defaultProps = {
  className: '',
  icon: null,
  placeholder: null,
  size: TextFieldWrapper.SIZE.STANDARD,
  error: false,
  onClear: null,
  errorLocation: ERROR_LOCATION.PLACEHOLDER,
  tooltipPosition: POSITIONS.TOP,
};

export default TextFieldWrapper;
